import {
  FleetAssetCapabilityType,
  fleetAssetCapabilityTypes,
  fleetAssetCapabilityTypesSelector,
  TrailerEntity,
  useGetTrailerCapabilitiesQuery
} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useCallback, useMemo} from "react";
import {
  FleetAssetCapabilityDataTable
} from "../../data-table/FleetAssetCapabilityDataTable/FleetAssetCapabilityDataTable";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {useTrailerMutation} from "../../../hooks/useTrailerMutation";
import {PAGINATION_PAGE_SIZES} from "../../data-table/DataTable/usePagination";

export type TrailerDetailsProps = {
  trailer: TrailerEntity
}

export const TrailerDetails = (props: TrailerDetailsProps) => {
  const {trailer} = props;

  const {t} = useTranslation(["common"]);


  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.trailer.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {trailer.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {trailer.companyId && <DefaultText value={trailer.companyName}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {trailer.freightTypeId && <DefaultText value={trailer.freightTypeName}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.size.label", "Capacity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedQuantity
                  value={{
                    number: trailer.size,
                    units: trailer.sizeUnitOfMeasure
                  }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.maxsize.label", "Max Capacity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedQuantity
                  value={{
                    number: trailer.maxSize,
                    units: trailer.sizeUnitOfMeasure
                  }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.weight.label", "Weight")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedQuantity
                  value={{
                    number: trailer.weight,
                    units: trailer.weightUnitOfMeasure
                  }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.trailer.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={trailer.doNotUse ? "warning" : "dimmed"} fz="xs">
                {trailer.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={6}>
        <TrailerCapabilityCard trailerId={trailer.id}/>
      </Grid.Col>
      <Grid.Col span={12}>
        <HasAccess permissions={["object_mapping_view"]}>
          <ObjectMappingDataTableCard entity={trailer}/>
        </HasAccess>
      </Grid.Col>
    </Grid>
  );
};

const TrailerCapabilityCard = (props: { trailerId: string }) => {
  const {trailerId} = props;

  const {t} = useTranslation(["common"]);

  const selectCapabilities = useMemo(() => {
    return fleetAssetCapabilityTypesSelector();
  }, []);

  const {activeCapabilities, isActiveCapabilitiesLoading} = useGetTrailerCapabilitiesQuery(trailerId, {
    selectFromResult: (result) => ({
      ...result,
      activeCapabilities: selectCapabilities(result),
      isActiveCapabilitiesLoading: result.isLoading,
    })
  });

  const {
    addCapabilities,
    removeCapabilities,
    isAddingCapabilities,
    isRemovingCapabilities,
  } = useTrailerMutation()

  const isCapabilitiesLoading = useMemo(() => {
    return isActiveCapabilitiesLoading || isAddingCapabilities || isRemovingCapabilities
  }, [isAddingCapabilities, isRemovingCapabilities, isActiveCapabilitiesLoading])

  const toggleCapabilities = useCallback((cap: FleetAssetCapabilityType) => {
    const caps: string[] = []
    caps.push(cap.code)

    const value = activeCapabilities.has(cap)
    if (value) {
      removeCapabilities(trailerId, {capabilities: caps}, undefined)
    } else {
      addCapabilities(trailerId, {capabilities: caps}, undefined)
    }
  }, [addCapabilities, removeCapabilities, trailerId, activeCapabilities])

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" pb="sm">
        <Title order={4}>{
          t("common.entity.trailer.profile.info.section.capabilities.label", "Capabilities")}
        </Title>
      </Group>

      <Divider my="md"/>

      <FleetAssetCapabilityDataTable
        permPrefix="trailer"
        selectedCapabilities={activeCapabilities}
        toggleCapability={toggleCapabilities}
        verticalSpacing="sm"
        withBorder
        records={fleetAssetCapabilityTypes}
        recordsPerPageOptions={PAGINATION_PAGE_SIZES}
        fetching={isCapabilitiesLoading}
      />
    </Card>
  )
}

